import { AppState, LogoutOptions, RedirectLoginOptions, useAuth0 } from '@auth0/auth0-react'
import { Browser } from '@capacitor/browser'

import { useEffect, useRef } from 'react'
import { useHistory, useParams } from 'react-router-dom'

import { Loading } from '../components/Loading'
import {
  AUTH0_LOGIN_SCOPE,
  cleanupAfterAuthDecline,
  getAuthErrorFromUrl,
  getPostDeclineLogoutReturnTo,
  isNativeApp,
} from '../config/auth0'
import { AuthStateContext } from '../providers/AuthStateContext'

const Login = (): JSX.Element => {
  const isMobileDevice = isNativeApp()
  const { isAuthenticated, isLoading, loginWithRedirect, logout } = useAuth0()
  const { redirect } = useParams<{ redirect?: string }>()
  const history = useHistory()
  const handledRef = useRef(false)

  const destination = redirect ? decodeURIComponent(redirect) : '/'

  useEffect(() => {
    if (isLoading || handledRef.current) return

    if (isAuthenticated) {
      handledRef.current = true
      history.replace(destination)
      return
    }

    if (getAuthErrorFromUrl()) {
      handledRef.current = true
      cleanupAfterAuthDecline()
      // Clear Auth0 SSO session (same as /redirect decline) so retry shows login/signup form.
      const logoutOptions: LogoutOptions = {
        logoutParams: {
          returnTo: getPostDeclineLogoutReturnTo(),
        },
      }
      if (isMobileDevice) {
        logoutOptions.openUrl = async (url) => {
          await Browser.open({
            url,
            windowName: '_self',
          })
        }
      }
      void logout(logoutOptions)
      return
    }

    // separate redirectUrl like this since mobile devices don't get appState after redirect
    AuthStateContext.setRedirectUrl(destination)

    let loginParams: RedirectLoginOptions<AppState> = {
      appState: { redirectUrl: destination },
      authorizationParams: { scope: AUTH0_LOGIN_SCOPE },
    }
    if (isMobileDevice) {
      loginParams = {
        ...loginParams,
        async openUrl(url) {
          // Redirect using Capacitor's Browser plugin
          await Browser.open({
            url,
            windowName: '_self',
          })
        },
      }
    }

    loginWithRedirect(loginParams)
  }, [destination, history, isAuthenticated, isLoading, isMobileDevice, loginWithRedirect, logout, redirect])

  return (
    <div className="bg-light-blue flex justify-center items-center min-h-screen relative">
      <Loading />
    </div>
  )
}

export default Login
